import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { SettingsHubKey } from '../components/settings/SettingsHubModal';
import type { PosTab, SettingsModalKey } from '../core/pos-shell-page.types';

type ReportTileKey = 'reportX' | 'restoreZ' | 'workerReport' | 'zcreditTransactions' | 'zReportLayout';

type UsePosSettingsUiParams = {
  setActiveTab: Dispatch<SetStateAction<PosTab>>;
  onNavigateKds?: () => void;
  onNavigateBackoffice?: () => void;
  onOpenCashDrawer?: () => void;
  onOpenReport?: (key: ReportTileKey) => void;
  onOpenTestPush?: () => void;
  onOpenVatRate?: () => void;
  onOpenHeaderEdit?: () => void;
  onOpenAccountantReport?: () => void;
  onOpenEditOpeningCash?: () => void;
  onOpenCashMove?: () => void;
  onSelfRefresh?: () => void;
  onBroadcastRefresh?: () => void;
  onOpenHappyHour?: () => void;
  onOpenTakeAway?: () => void;
  onOpenQuickOrder?: () => void;
  onOpenDailyBrief?: () => void;
  onOpenQuickCharge?: () => void;
  onToggleKioskMode?: () => void;
};

/**
 * Architectural summary: Owns PosShell settings-hub/modal visibility and tile
 * selection transitions, including the Areas Edit routing shortcut.
 */
export function usePosSettingsUi({
  setActiveTab,
  onNavigateKds,
  onNavigateBackoffice,
  onOpenCashDrawer,
  onOpenReport,
  onOpenTestPush,
  onOpenVatRate,
  onOpenHeaderEdit,
  onOpenAccountantReport,
  onOpenEditOpeningCash,
  onOpenCashMove,
  onSelfRefresh,
  onBroadcastRefresh,
  onOpenHappyHour,
  onOpenTakeAway,
  onOpenQuickOrder,
  onOpenDailyBrief,
  onOpenQuickCharge,
  onToggleKioskMode,
}: UsePosSettingsUiParams) {
  const [settingsHubOpen, setSettingsHubOpen] = useState(false);
  const [settingsModalKey, setSettingsModalKey] = useState<SettingsModalKey | null>(null);
  const [areaMode, setAreaMode] = useState<'view' | 'edit'>('view');
  const [isSetMinimumMode, setIsSetMinimumMode] = useState(false);

  const isSettingsActive = settingsHubOpen || settingsModalKey !== null;
  // The bare hub menu is just a menu, not work-in-progress; only a sub-flow blocks a refresh.
  const isSettingsSubFlowActive = settingsModalKey !== null;

  /**
   * Architectural summary: Maps a settings hub tile selection to either a
   * settings sub-modal or the Areas edit-mode route within PosShell.
   */
  const handleSelectSettingsTile = useCallback((key: SettingsHubKey) => {
    if (key === 'refresh') {
      setSettingsHubOpen(false);
      onSelfRefresh?.();
      return;
    }

    if (key === 'refreshAll') {
      setSettingsHubOpen(false);
      onBroadcastRefresh?.();
      return;
    }

    if (key === 'areasEdit') {
      setSettingsHubOpen(false);
      setActiveTab('areas');
      setAreaMode('edit');
      return;
    }

    if (key === 'minOrder') {
      setSettingsHubOpen(false);
      setActiveTab('areas');
      setAreaMode('view');
      setIsSetMinimumMode(true);
      return;
    }

    if (key === 'kds') {
      setSettingsHubOpen(false);
      onNavigateKds?.();
      return;
    }

    if (key === 'backoffice') {
      setSettingsHubOpen(false);
      onNavigateBackoffice?.();
      return;
    }

    if (key === 'openDrawer') {
      // Fires the drawer-open action directly (no sub-modal).
      setSettingsHubOpen(false);
      onOpenCashDrawer?.();
      return;
    }

    if (key === 'happyHour') {
      setSettingsHubOpen(false);
      onOpenHappyHour?.();
      return;
    }

    if (key === 'takeAway') {
      setSettingsHubOpen(false);
      onOpenTakeAway?.();
      return;
    }

    if (key === 'quickOrder') {
      setSettingsHubOpen(false);
      onOpenQuickOrder?.();
      return;
    }

    if (key === 'dailyBrief') {
      setSettingsHubOpen(false);
      onOpenDailyBrief?.();
      return;
    }

    if (key === 'quickCharge') {
      setSettingsHubOpen(false);
      onOpenQuickCharge?.();
      return;
    }

    if (key === 'kioskMode') {
      setSettingsHubOpen(false);
      setActiveTab('orders');
      setAreaMode('view');
      onToggleKioskMode?.();
      return;
    }

    if (key === 'openingCash') {
      setSettingsHubOpen(false);
      onOpenEditOpeningCash?.();
      return;
    }

    if (key === 'cashMove') {
      setSettingsHubOpen(false);
      onOpenCashMove?.();
      return;
    }

    if (key === 'testPush') {
      setSettingsHubOpen(false);
      onOpenTestPush?.();
      return;
    }

    if (key === 'reportX' || key === 'restoreZ' || key === 'workerReport' || key === 'zcreditTransactions' || key === 'zReportLayout') {
      setSettingsHubOpen(false);
      onOpenReport?.(key);
      return;
    }

    if (key === 'vatRate') {
      setSettingsHubOpen(false);
      onOpenVatRate?.();
      return;
    }

    if (key === 'headerEdit') {
      setSettingsHubOpen(false);
      onOpenHeaderEdit?.();
      return;
    }

    if (key === 'accountantReport') {
      setSettingsHubOpen(false);
      onOpenAccountantReport?.();
      return;
    }

    setSettingsHubOpen(false);
    setSettingsModalKey(key);
  }, [onNavigateKds, onNavigateBackoffice, onOpenCashDrawer, onOpenHappyHour, onOpenTakeAway, onOpenQuickOrder, onOpenDailyBrief, onOpenQuickCharge, onToggleKioskMode, onOpenReport, onOpenTestPush, onOpenVatRate, onOpenHeaderEdit, onOpenAccountantReport, onOpenEditOpeningCash, onOpenCashMove, onSelfRefresh, onBroadcastRefresh, setActiveTab]);

  return {
    settingsHubOpen,
    setSettingsHubOpen,
    settingsModalKey,
    setSettingsModalKey,
    areaMode,
    setAreaMode,
    isSetMinimumMode,
    setIsSetMinimumMode,
    isSettingsActive,
    isSettingsSubFlowActive,
    handleSelectSettingsTile,
  };
}
